import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Trophy, Lock, Eye, EyeOff, CheckCircle } from "lucide-react";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setDone(true);
      toast.success("Password reset successful!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-white border-gray-200 shadow-sm text-center">
          <CardContent className="py-12">
            <Lock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid Reset Link</h2>
            <p className="text-gray-500 mb-6">This password reset link is invalid or has expired.</p>
            <Button onClick={() => navigate("/admin123okay/login")} className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6">
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-white border-gray-200 shadow-sm text-center">
          <CardContent className="py-12">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Password Reset!</h2>
            <p className="text-gray-500 mb-6">Your password has been updated. You can now log in.</p>
            <Button onClick={() => navigate("/admin123okay/login")} className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6" data-testid="go-login-btn">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center px-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md bg-white border-gray-200 shadow-sm">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Trophy className="h-8 w-8 text-green-600" />
            <span className="text-2xl font-bold logo-text">FIXEDRAWS</span>
          </div>
          <CardTitle className="text-xl font-bold text-gray-900 flex items-center justify-center gap-2">
            <Lock className="h-5 w-5" /> Set New Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700">New Password</Label>
              <div className="relative">
                <Input
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  placeholder="Min 6 characters"
                  className="h-11 pr-11"
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Confirm Password</Label>
              <Input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                type="password" placeholder="Re-enter password" className="h-11" data-testid="confirm-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold" data-testid="reset-submit-btn">
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
